import { useState, useEffect } from "react";
import { Typography, Menu, Dropdown, Modal, Badge, Tooltip, Switch } from "antd";
import {
  BellOutlined,
  LogoutOutlined,
  DownloadOutlined,
  SwapOutlined,
  RetweetOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons";
import { logOut } from "helpers/generalHelpers";
import { AccountIcon, BellIcon, Settings as SettingsIcon } from "components/Icons";
import { VERSION_NUMBER } from "config";
import SearchAPI from "./GlobalSearch/SearchAPI";
import AppSwitcher from "../AppSwitcher/AppSwitcher";
import { fetchDequeuedItems } from "helpers/forceSync";
import { exportDeviceData, exportUnsyncedData, exportDataIssues } from "helpers/backupLocalData";
import OnlineStatusIndicator from "./OnlineStatusIndicator/OnlineStatusIndicator";
import { fetchHierarchyManifest, hierarchyExists } from "helpers/hierarchyHelpers";
import { useEntityData } from "EntityDataContext/EntityDataContext";
import MenuDivider from "antd/lib/menu/MenuDivider";
import { ROLES } from "components/AppSwitcher/helpers/roles"; // CAN BE REMOVED WHEN FEATURE FLAG REMOVED ON DATA DOWNLOAD OPTIONS
import { useOnlineContext } from "helpers/contexts/OnlineContext/OnlineContext";
import "./AppHeader.scss";

const DisplaySettingsMenu = () => {
  const {
    switchTenantsActive,
    setShowSwitchAccount,
    downloadAllImages,
    setDownloadAllImages,
    tenantData,
    userData, // CAN BE REMOVED WHEN FEATURE FLAG REMOVED ON DATA DOWNLOAD OPTIONS
    restartDataStore
  } = useEntityData()

  const { isOnline } = useOnlineContext()

  const { name: tenantName } = tenantData
  const { roles } = userData // CAN BE REMOVED WHEN FEATURE FLAG REMOVED ON DATA DOWNLOAD OPTIONS

  function tooltipText() {
    if (!isOnline) return 'You can\'t switch account whilst offline.'
    if (!switchTenantsActive) return 'You can\'t switch account as you only have access to 1 account.'
    return 'Click here to switch account.'
  }

  const [hierarchyManifest, setHierarchyManifest] = useState()

  const fetchLatestTime = async () => {
    const exists = await hierarchyExists(window.tenantId)
    if (!exists) return
    const manifest = await fetchHierarchyManifest()
    setHierarchyManifest(manifest)
  }

  useEffect(() => {
    fetchLatestTime()
  }, [])

  return (
    <Menu>
      <Tooltip
        title={tooltipText()}
        placement={'bottom'}
      >
        <div>
          <Menu.Item
            icon={<SwapOutlined />}
            key="select-account"
            onClick={() => { setShowSwitchAccount((showSwitchAccount) => !showSwitchAccount) }}
            disabled={!isOnline || !switchTenantsActive}
          >
            Select Account
          </Menu.Item>
        </div>

      </Tooltip>
      <MenuDivider />
      
        <Menu.Item
          key="offline-images"
          onClick={(e) => {
            setDownloadAllImages(!downloadAllImages)
          }}
        >
          <div className="download-images-menu-item-wrapper">
            <div className="offline-switch-wrapper">
              <Switch
                className="offline-switch"
                loading={false}
                checked={downloadAllImages}
                size="small"
                onClick={(_, e) => {
                  e.stopPropagation()
                }}
                onChange={(checked, e) => {
                  e.stopPropagation()
                  setDownloadAllImages(checked)
                }}
              />
              <span className={downloadAllImages ? 'download-images-toggle-label-bold' : ''}>
                Download images {downloadAllImages ? '(on)' : '(off)'}
              </span>
            </div>
            <Tooltip
              title={(
                <>
                  <strong>When enabled (on/green)</strong> - all images for your active facilities will be downloaded and can be viewed offline.
                  <br /><br />
                  <strong>If not enabled (off/grey)</strong>  - you will only see images you have added.
                </>
              )}
              placement="left"
            >
              <QuestionCircleOutlined onClick={(e) => e.stopPropagation()} />
            </Tooltip>
          </div>
        </Menu.Item>

      <Menu.Divider />
        <Menu.Item
          icon={<RetweetOutlined />}
          key="restart-database"
          onClick={() => {
            Modal.confirm({
              className: "datastore-confirmation-modal",
              title: "Restart DataStore",
              content: (
                <p>
                  Restarting the Data Storage could cause data loss on some devices. Are you sure you want to perform this action?
                </p>
              ),
              icon: <RetweetOutlined className="datastore-confirmation-icon--warning" />,
              okText: "Continue",
              cancelText: "Cancel",
              width: 600,
              onOk: () => restartDataStore(),
            });
          }}
        >
          Restart DataStore
        </Menu.Item>
        {/* TEMPORARY FEATURE FLAG TO LIMIT DEVICE DATA DOWNLOAD TO REAMS ADMINS ONLY */}
        {roles.includes(ROLES.ADMIN) && (
          <>
            <Menu.Item
              icon={<BellOutlined />}
              key="download-backup-data-issues-report"
              onClick={() => {
                Modal.confirm({
                  className: "datastore-confirmation-modal",
                  title: "Data Issues Report",
                  content: (
                    <p>
                      Download your sync issue data. Please send the file to ELIAS support.<br/>
                      The data file can be found in your device's Downloads folder.
                    </p>
                  ),
                  icon: <BellOutlined className="datastore-confirmation-icon--errors" />,
                  okText: "Download",
                  cancelText: "Cancel",
                  width: 600,
                  onOk: () => exportDataIssues(tenantName)
                });
              }}
            >
              Data Issues Report
            </Menu.Item>
            <Menu.Item
              icon={<DownloadOutlined />}
              key="download-backup-unsynced"
              onClick={() => {
                Modal.confirm({
                  className: "datastore-confirmation-modal",
                  title: "Export Unsynced Data",
                  content: (
                    <p>
                      Download your unsynced data. Please send the file to ELIAS support.<br/>
                      The data file can be found in your device's Downloads folder.
                    </p>
                  ),
                  icon: <DownloadOutlined className="datastore-confirmation-icon" />,
                  okText: "Download",
                  cancelText: "Cancel",
                  width: 600,
                  onOk: () => exportUnsyncedData(tenantName)
                });
              }}
            >
              Export Unsynced Data
            </Menu.Item>
            <Menu.Item
              icon={<DownloadOutlined />}
              key="download-backup-datastore"
              onClick={() => {
                Modal.confirm({
                  className: "datastore-confirmation-modal",
                  title: "Export All Data",
                  content: (
                    <p>
                      Download your device data. Please send the file to ELIAS support.<br/>
                      The data file can be found in your device's Downloads folder.
                    </p>
                  ),
                  icon: <DownloadOutlined className="datastore-confirmation-icon"/>,
                  okText: "Download",
                  cancelText: "Cancel",
                  width: 600,
                  onOk: () => exportDeviceData(tenantName)
                });
              }}
            >
              Export All Data
            </Menu.Item>
          </>
        )}
      <Menu.Divider />
      <Menu.Item
        key="hierarchy-details"
        style={{ pointerEvents: 'none' }}
        onClick={(e) => {
          // NOTE: for AntD Menu.Item: e.preventDefault() is not a valid
          // function to prevent menu item select behaviour and throws error in console.
          // Add the style prop {{ pointerEvents: 'none' }} to prevent the menu item being selectable
          fetchLatestTime()
        }}
      >
        {!hierarchyManifest ? 'Hierarchy does not exist' : `Hierarchy last validated at: ${hierarchyManifest?.lastDownloaded && (new Date(hierarchyManifest?.lastDownloaded)).toLocaleString()}`}
      </Menu.Item>
    </Menu>
  );
}


export default function AppHeader() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dequeuedCount, setDequeuedCount] = useState(0);

  const { syncStatusHook: { isSyncingData }, user, tenantData } = useEntityData()
  
  const { isOnline } = useOnlineContext()

  useEffect(() => {
    fetchDequeuedItems().then((items) => setDequeuedCount(items.length || 0));
  }, [isSyncingData]);

  const logoutTooltipText = () => {
    if (!isOnline) {
      return "You can't logout whilst offline."
    }

    if (isSyncingData) {
      return "You can't logout whilst having unsynced data or syncing in progress."
    }

    return "Click here to logout."
  }

  function displayAccountMenu() {
    return (
      <Menu>
        <Menu.Item key="email" disabled>
          Email: {user?.idToken.payload.email}
        </Menu.Item>
        <Menu.Item key="tenant" disabled>
          Tenant: {tenantData.name}
        </Menu.Item>
        <Menu.Divider />
        <Tooltip
          title={logoutTooltipText()}
          placement={'bottom'}
        >
          <Menu.Item
            icon={<LogoutOutlined />}
            key="logout"
            onClick={() => logOut(isOnline)}
            tooltipText="Logout"
            disabled={!isOnline || isSyncingData}
          >
            Logout
          </Menu.Item>
        </Tooltip>
      </Menu>
    );
  }

  return (
    <div className="app-header">
      <p className="version-number">v{VERSION_NUMBER}</p>
      <div className="menu-button">
        <AppSwitcher />
      </div>
      <div className="menu-button-separator" />
      <div className="header-title-wrapper">
        <Tooltip
          title={tenantData.name}
          placement="bottom"
        >
          <Typography.Title
            level={1}
            className="header-title"
            style={{ maxWidth: "100%", fontSize: tenantData.name.length >= 16 ? '28px' : '32px' }}
          >
            {tenantData.name}
          </Typography.Title>
        </Tooltip>
      </div>
      <div className="account-and-status">
        <SearchAPI
          dropdownOpen={dropdownOpen}
          setDropdownOpen={setDropdownOpen}
        />
        <div style={{ display: "flex", float: "right" }}>
          <OnlineStatusIndicator />
          <div style={{ paddingRight: dequeuedCount > 0 ? "20px" : undefined }}>
            <Badge count={dequeuedCount}>
              <div className="notification-button">
                <BellIcon />
              </div>
            </Badge>
          </div>
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            overlay={<DisplaySettingsMenu />}
          >
            <div className="settings-button">
              <SettingsIcon />
            </div>
          </Dropdown>
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            overlay={displayAccountMenu()}
          >
            <div className="account-button">
              <AccountIcon />
            </div>
          </Dropdown>
        </div>
      </div>
    </div>
  );
}
